import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Store, ClipboardList, MessageSquareWarning, Truck, History } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";
import StockHistoryTable from "@/components/StockHistoryTable";

export default function AdminMitraPortal() {
  const [stores, setStores] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [stockHistory, setStockHistory] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data));
    api.get("/admin/opname-status").then((r) => setStatus(r.data));
  }, []);

  const openStore = async (s) => {
    setSelected(s); setData(null); setStockHistory(null);
    try {
      const { data } = await api.get(`/admin/mitra-portal/${s.id}`);
      setData(data);
    } catch (e) { console.error(e); }
    try {
      const { data: hist } = await api.get(`/admin/stores/${s.id}/stock-history`);
      setStockHistory(hist);
    } catch (e) { console.error(e); }
  };

  const tf = useTableFilter(stores, {
    searchKeys: ["name", "pic_name", "address"],
    defaultSort: { key: "name", dir: "asc" },
  });

  return (
    <>
      <PageHeader title="Portal Mitra Toko Binaan" subtitle="Lihat data mitra: rekap stok, penyaluran ke retail, riwayat stok & opname (read-only)" testId="page-admin-mitra"/>

      {status && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
            <div className="overline mb-2">Total Toko Binaan</div>
            <div className="font-mono-num text-2xl font-bold">{status.total_stores}</div>
          </Card>
          <Card className="p-5 border-2 border-emerald-300 bg-emerald-50/40 rounded-md stat-card">
            <div className="overline mb-2">Sudah Opname Bulan Ini</div>
            <div className="font-mono-num text-2xl font-bold text-emerald-700">{status.submitted}</div>
            <div className="text-xs text-slate-500 mt-1">Periode {status.month}</div>
          </Card>
          <Card className="p-5 border-2 border-amber-300 bg-amber-50/40 rounded-md stat-card">
            <div className="overline mb-2">Belum Opname</div>
            <div className="font-mono-num text-2xl font-bold text-amber-700">{status.pending}</div>
            <div className="text-xs text-slate-500 mt-1">Perlu reminder setelah tgl 8</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="list">
        <TabsList className="mb-4">
          <TabsTrigger value="list" data-testid="tab-admin-list"><Store className="w-4 h-4 mr-2"/>Daftar Toko</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-admin-status"><ClipboardList className="w-4 h-4 mr-2"/>Status Opname Bulan Ini</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b"><TableToolbar tf={tf} placeholder="Cari toko..."/></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortHeader tf={tf} col="name">Nama Toko</SortHeader>
                <SortHeader tf={tf} col="pic_name">PIC</SortHeader>
                <SortHeader tf={tf} col="pic_wa">No. WA</SortHeader>
                <SortHeader tf={tf} col="address">Alamat</SortHeader>
                <TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {tf.rows.map((s) => (
                  <TableRow key={s.id} className="table-row-hover">
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>{s.pic_name || "-"}</TableCell>
                    <TableCell className="font-mono-num text-sm">{s.pic_wa || "-"}</TableCell>
                    <TableCell className="text-slate-500 text-sm max-w-md truncate">{s.address || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openStore(s)} data-testid={`btn-view-${s.id}`}>Lihat Portal</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="status">
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Toko</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tanggal Opname</TableHead>
                <TableHead>Oleh</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(status?.stores || []).map((s) => (
                  <TableRow key={s.store_id} className="table-row-hover">
                    <TableCell className="font-medium">{s.store_name}</TableCell>
                    <TableCell>
                      {s.submitted
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">✓ Sudah</Badge>
                        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">⌛ Belum</Badge>}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm">{s.opname_date ? formatDate(s.opname_date) : "-"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{s.submitted_by || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openStore({ id: s.store_id, name: s.store_name })}>Detail</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Portal Mitra: {selected?.name}</DialogTitle></DialogHeader>
          {!data ? <div className="text-slate-500 py-8 text-center">Memuat...</div> : (
            <div className="space-y-5">
              {/* Info identitas */}
              <div className="grid grid-cols-2 gap-3 text-sm p-3 bg-slate-50 rounded">
                <div><b>PIC:</b> {data.store?.pic_name || "-"}</div>
                <div><b>WA:</b> {data.store?.pic_wa || "-"}</div>
                <div><b>Kontak:</b> {data.store?.contact || "-"}</div>
                <div><b>Jam Op.:</b> {data.store?.opening_hours || "-"}</div>
                <div className="col-span-2"><b>Alamat:</b> {data.store?.address || "-"}</div>
              </div>

              {/* Aggregate stock */}
              <div>
                <div className="overline mb-2">Rekap Stok Titipan (Sistem)</div>
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Produk</TableHead>
                    <TableHead className="text-right">Total Kirim</TableHead>
                    <TableHead className="text-right">Dilaporkan Jual</TableHead>
                    <TableHead className="text-right">Saldo Sistem</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(data.aggregate || []).map((p) => (
                      <TableRow key={p.product_id}>
                        <TableCell className="font-medium">{p.product_name}</TableCell>
                        <TableCell className="text-right font-mono-num">{p.total_kirim}</TableCell>
                        <TableCell className="text-right font-mono-num text-emerald-700">{p.total_lapor}</TableCell>
                        <TableCell className="text-right font-mono-num font-bold text-[var(--hotto-primary)]">{p.saldo_sistem}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Penyaluran ke Retail (Kiriman) */}
              <div>
                <div className="overline mb-2 flex items-center gap-2"><Truck className="w-4 h-4"/>Laporan Penyaluran ke Retail ({(data.shipments || []).length})</div>
                {(data.shipments || []).length === 0 ? <div className="text-sm text-slate-500 py-2">Belum ada penyaluran.</div> : (
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Tanggal</TableHead>
                      <TableHead>Produk</TableHead>
                      <TableHead className="text-right">Total Qty</TableHead>
                      <TableHead className="text-right">Nilai</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {data.shipments.map((d) => {
                        const qty = (d.items || []).reduce((s, it) => s + Number(it.qty || 0), 0);
                        return (
                          <TableRow key={d.id}>
                            <TableCell className="font-mono-num text-sm whitespace-nowrap">{formatDate(d.date || d.created_at)}</TableCell>
                            <TableCell className="text-sm">{(d.items || []).map((it) => `${it.product_name} (${it.qty})`).join(", ")}</TableCell>
                            <TableCell className="text-right font-mono-num">{qty}</TableCell>
                            <TableCell className="text-right font-mono-num text-slate-600">{formatIDR(d.total || 0)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Riwayat Pergerakan Stok Gabungan (Kartu Stok) */}
              <div>
                <div className="overline mb-2 flex items-center gap-2"><History className="w-4 h-4"/>Riwayat Pergerakan Stok (Stok Awal + Pengiriman + Penjualan)</div>
                {stockHistory ? (
                  <StockHistoryTable data={stockHistory} storeName={selected?.name} testPrefix="admin-stock-history" />
                ) : (
                  <div className="text-sm text-slate-500 py-2">Memuat riwayat stok...</div>
                )}
              </div>

              {/* Riwayat opname */}
              <div>
                <div className="overline mb-2">Riwayat Opname ({(data.opname_reports || []).length})</div>
                {(data.opname_reports || []).length === 0 ? <div className="text-sm text-slate-500 py-2">Belum ada opname.</div> : (
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Tgl Opname</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Total Selisih</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {data.opname_reports.slice(0, 5).map((o) => {
                        const tv = (o.items || []).reduce((s, it) => s + Number(it.variance || 0), 0);
                        return (
                          <TableRow key={o.id}>
                            <TableCell className="font-mono-num text-sm">{formatDate(o.opname_date)}</TableCell>
                            <TableCell>{(o.items || []).length} produk</TableCell>
                            <TableCell className={`text-right font-mono-num ${tv === 0 ? "text-slate-400" : "text-red-600"}`}>{tv}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Keluhan */}
              <div>
                <div className="overline mb-2">Keluhan / Reminder ({(data.complaints || []).length})</div>
                {(data.complaints || []).length === 0 ? <div className="text-sm text-slate-500 py-2">Belum ada keluhan.</div> : (
                  <div className="space-y-2">
                    {data.complaints.slice(0, 10).map((c) => (
                      <div key={c.id} className="p-3 border border-slate-200 rounded text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="uppercase text-[10px]">{c.category}</Badge>
                          <Badge className={c.priority === "high" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>{c.priority}</Badge>
                          <span className="text-xs text-slate-500 ml-auto">{formatDateTime(c.created_at)}</span>
                        </div>
                        <div className="font-medium">{c.subject}</div>
                        <div className="text-xs text-slate-600 whitespace-pre-line">{c.message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
